import { Q } from "@nozbe/watermelondb";
import { useDatabase } from "@nozbe/watermelondb/hooks";

import { useCallback } from "react";
import Product from "../data/model/Product";
import { useOfflineQuery } from "../offline-search";
import { DEFAULT_SEARCH_LIMIT } from "../offline-search/constants";
import { OfflineSearchOptions } from "../offline-search/types";

export function useDatabaseProductHelpers() {
  const database = useDatabase();

  async function findProductByStockNumber(
    stockNumber: string
  ): Promise<Product | undefined> {
    const productCollection = database.collections.get<Product>("products");

    const product = productCollection
      .query(Q.where("stock_number", stockNumber), Q.take(1))
      .fetch()
      .then((res) => res[0]);

    return product;
  }

  async function findProductById(
    productId: string
  ): Promise<Product | undefined> {
    const productCollection = database.collections.get<Product>("products");

    return productCollection.find(productId);
  }

  return {
    findProductByStockNumber,
    findProductById,
  };
}

type ProductFilters = {
  categoryId?: string[];
  departmentId?: string[];
};

export const useMakeBaseProductQuery = () => {
  const database = useDatabase();

  const productCollection = database.collections.get<Product>("products");

  const makeBaseProductQuery = useCallback(
    (term: string, options?: OfflineSearchOptions<ProductFilters>) => {
      const sanitizedTerm = Q.sanitizeLikeString(term);

      const filters = options?.filters;

      const filterQueries = [];

      if (filters?.categoryId) {
        filterQueries.push(Q.where("category_id", Q.oneOf(filters.categoryId)));
      }

      if (filters?.departmentId) {
        filterQueries.push(
          Q.where("department_id", Q.oneOf(filters.departmentId))
        );
      }

      const qb = productCollection.query(
        Q.and(
          Q.or(
            Q.where("name", Q.like(`%${sanitizedTerm}%`)),
            Q.where("stock_number", Q.like(`%${sanitizedTerm}%`)),
            Q.where("stock_code", Q.like(`%${sanitizedTerm}%`))
          ),
          Q.where("variant_parent_id", Q.eq(null)),
          Q.where("active", Q.eq(true)),
          ...filterQueries
        )
      );

      return qb;
    },
    []
  );

  return {
    makeBaseProductQuery,
  };
};

export const useOfflineProductSearch = () => {
  const { doQuery, ...result } = useOfflineQuery<Product>();
  const { makeBaseProductQuery } = useMakeBaseProductQuery();

  const searchProducts = useCallback(
    (term: string, options?: OfflineSearchOptions<ProductFilters>) => {
      const qb = makeBaseProductQuery(term, options);

      const extendedQuery = qb.extend(
        Q.take(options?.limit || DEFAULT_SEARCH_LIMIT)
      );

      return doQuery(extendedQuery);
    },
    []
  );

  return {
    searchProducts,
    ...result,
  };
};
